// RightClickMenu.component.tsx
import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@store/index'
import { toggleSidePanel } from '@store/reducers/uiSlice.reducer'
import {
  BeforeOpenCloseMenuEventArgs,
  MenuEventArgs as ContextMenuEventArgs,
  MenuItemModel,
  ContextMenuComponent,
} from '@syncfusion/ej2-react-navigations'
import {
  closest,
  isNullOrUndefined,
  removeClass,
  remove,
} from '@syncfusion/ej2-base'
import { fetchPatientById } from '@services/api/patient.api'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import {
  AppointmentStatus,
  PatientWithInsurance,
} from '@dataTypes/appointment.type'
import { APPT_STATUS_MENU_ITEMS } from '@constants/AppointmentPage'
import { ScheduleComponent } from '@syncfusion/ej2-react-schedule'
import { useAppSelector } from '@store/hooks'
import { SelectContactModal } from '@components/layout/Modals'
import { IContact } from '@dataTypes/contact.type'
import useContact from '../Messages/MessageCenterContact/useContact'
import {
  ChatDirection,
  IChatConversation,
  SendStatus,
} from '@dataTypes/chat.type'
import { createSms } from '@store/actions'
import NotifyPatient from '@components/layout/Modals/NotifyPatient/NotifyPatient.component'
import { getEnv } from '@utils/env'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { getFullName } from '@utils/fullname.util'
import moment from 'moment'

import { logger } from '@utils/safeLogger'

/** Formats "Notes for [Patient Name] (02/11/26 14:00)" for Notes title. */
export function formatAppointmentContextName(event: {
  patient?: { first_name?: string; last_name?: string; middle_name?: string }
  appointment_start_date?: string
  appt_date?: string
  appt_start?: string
}): string {
  const patientName =
    event.patient != null
      ? getFullName(
          {
            first_name: event.patient.first_name ?? '',
            last_name: event.patient.last_name ?? '',
            middle_name: event.patient.middle_name,
          },
          false,
          false,
          'Patient',
        ) || 'Patient'
      : 'Patient'

  let dateTime: string
  if (event.appointment_start_date) {
    dateTime = moment(event.appointment_start_date).format('MM/DD/YY HH:mm')
  } else if (event.appt_date && event.appt_start) {
    const datePart = moment(event.appt_date).format('MM/DD/YY')
    const timePart = event.appt_start.slice(0, 5)
    dateTime = `${datePart} ${timePart}`
  } else {
    dateTime = ''
  }

  return dateTime ? `${patientName} (${dateTime})` : patientName
}

type AppointmentNoteOverlayInput = {
  appointmentId: string
  patientId: string
  contextName: string
}

type RightClickMenu = {
  scheduleObj: React.RefObject<ScheduleComponent>
  openCallConfirmationModal: (number: string, name: string) => void
  openDeleteModal: (apptId?: string) => void
  openEmailModal: (email: string) => void
  handleUpdateAppointmentStatus: (
    appointment_id: string,
    status: number,
  ) => Promise<void>
  onItemSelected?: () => void
  targetCalendarContainer?: string
  onViewNote?: (input: AppointmentNoteOverlayInput) => void
}

const RightClickMenu: React.FC<RightClickMenu> = ({
  scheduleObj,
  openCallConfirmationModal,
  openDeleteModal,
  openEmailModal,
  handleUpdateAppointmentStatus,
  onItemSelected,
  targetCalendarContainer = 'e-schedule',
  onViewNote,
}) => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { ...contactsHooks } = useContact()
  const conversations = useAppSelector((state) => state.chat.data)
  const contacts = useAppSelector((state) => state.contacts.data)
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)
  const [notifyPatientModal, setNotifyPatientModal] = useState<{
    show: boolean
    appointmentInfo?: any
  }>({
    show: false,
    appointmentInfo: undefined,
  })
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const contextMenuObj = useRef<ContextMenuComponent>(null)
  let selectedTarget: Element

  const contextMenuItems: MenuItemModel[] = [
    {
      text: APPT_STATUS_MENU_ITEMS[AppointmentStatus.Confirmed].text,
      iconCss: 'ddb-icons mgc_check_line dd-icon',
      id: APPT_STATUS_MENU_ITEMS[AppointmentStatus.Confirmed].id.replace(
        'status-',
        '',
      ),
    },
    {
      text: APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckIn].text,
      iconCss: 'ddb-icons mgc_user_follow_2_fill dd-icon',
      id: APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckIn].id.replace(
        'status-',
        '',
      ),
    },
    {
      text: APPT_STATUS_MENU_ITEMS[AppointmentStatus.InTreatment].text,
      iconCss: 'ddb-icons mgc_dental_fill dd-icon',
      id: APPT_STATUS_MENU_ITEMS[AppointmentStatus.InTreatment].id.replace(
        'status-',
        '',
      ),
    },
    {
      text: APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckOut].text,
      iconCss: 'ddb-icons mgc_arrow_right_line dd-icon',
      id: APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckOut].id.replace(
        'status-',
        '',
      ),
    },
    {
      text: APPT_STATUS_MENU_ITEMS[AppointmentStatus.Completed].text,
      iconCss: 'ddb-icons mgc_check_circle_fill dd-icon',
      id: APPT_STATUS_MENU_ITEMS[AppointmentStatus.Completed].id.replace(
        'status-',
        '',
      ),
    },
    {
      text: 'Status',
      iconCss: 'ddb-icons mgc_loading_2_line dd-icon',
      id: 'status',
      items: Object.entries(APPT_STATUS_MENU_ITEMS)
        .map(([status, item]) => ({ status, ...item }))
        .sort((a, b) => a.position - b.position),
    },
    {
      text: 'Go to',
      iconCss: 'ddb-icons mgc_arrow_up_line dd-icon',
      id: 'goto',
      items: [
        { text: 'Treatment plan', id: 'goto-treatmentplan' },
        { text: 'Perio charting', id: 'goto-periocharting' },
        { text: 'X-ray imaging', id: 'goto-xrayimaging' },
        { text: 'Lab order', id: 'goto-laborder' },
        { text: 'Check coverage', id: 'goto-checkcoverage' },
        { text: 'Reschedule', id: 'goto-reschedule' },
        { text: 'Rq pre-auth', id: 'goto-rqpreauth' },
        { text: 'Rq claim', id: 'goto-rqclaim' },
      ],
    },
    {
      text: 'View info',
      iconCss: 'ddb-icons mgc_eye_2_fill dd-icon',
      id: 'info',
      items: [
        { text: 'Patient info', id: 'info-patient' },
        { text: 'Appt. info', id: 'info-appt' },
      ],
    },
    {
      text: 'Contact',
      iconCss: 'ddb-icons mgc_send_fill dd-icon',
      id: 'contact',
      items: [
        { text: 'Call', id: 'call' },
        { text: 'Message', id: 'message' },
        { text: 'Email', id: 'email' },
      ],
    },
    {
      text: 'Refer to',
      iconCss: 'ddb-icons mgc_department_fill dd-icon',
      id: 'referto',
      items: [],
    },
    {
      text: 'Notify',
      iconCss: 'ddb-icons mgc_bell_ringing_fill dd-icon',
      id: 'notify',
    },
    {
      text: 'Add note',
      iconCss: 'ddb-icons mgc_edit_4_fill dd-icon',
      id: 'addnote',
    },
    {
      text: 'Cancel appt.',
      iconCss: 'ddb-icons mgc_close_circle_fill dd-icon',
      id: 'cancel',
    },
  ]

  const contextMenuOpen = (args: BeforeOpenCloseMenuEventArgs): void => {
    const newEventElement: HTMLElement = document.querySelector(
      '.e-new-event',
    ) as HTMLElement
    if (newEventElement) {
      remove(newEventElement)
      removeClass(
        [document.querySelector('.e-selected-cell') as Element],
        'e-selected-cell',
      )
    }
    scheduleObj?.current?.closeQuickInfoPopup()
    const targetElement: HTMLElement = args.event.target as HTMLElement
    if (closest(targetElement, '.e-contextmenu')) {
      return
    }
    selectedTarget = closest(
      targetElement,
      '.e-appointment,.e-work-cells,.e-vertical-view .e-date-header-wrap .e-all-day-cells,.e-vertical-view .e-date-header-wrap .e-header-cells',
    )
    if (isNullOrUndefined(selectedTarget)) {
      args.cancel = true
      return
    }
    if (selectedTarget.classList.contains('e-appointment')) {
      const eventObj: Record<string, any> =
        scheduleObj?.current?.getEventDetails(selectedTarget) as Record<
          string,
          any
        >
      if (eventObj.RecurrenceRule) {
        contextMenuObj?.current?.showItems([], true)
        contextMenuObj?.current?.hideItems(['cancel'], true)
      } else {
        let statusToBeShown: AppointmentStatus = AppointmentStatus.Confirmed
        switch (eventObj.appt_status) {
          case 0: // Confirmed
            statusToBeShown = AppointmentStatus.CheckIn
            break
          case 7: // No Show
            statusToBeShown = AppointmentStatus.CheckIn
            break
          case 8: // Late
            statusToBeShown = AppointmentStatus.CheckIn
            break
          case 9: // Unconfirmed
            statusToBeShown = AppointmentStatus.Confirmed
            break
          case 10: // Check-in
            statusToBeShown = AppointmentStatus.InTreatment
            break
          case 11: // In-treatment
            statusToBeShown = AppointmentStatus.CheckOut
            break
          case 12: // Check-out
            statusToBeShown = AppointmentStatus.Completed
            break
        }

        const statussesToBeHidden = Object.entries(APPT_STATUS_MENU_ITEMS)
          .filter(
            ([key, value]) =>
              key !== statusToBeShown.toString() &&
              value.id !== 'status-unconfirmed' &&
              value.id !== 'status-late' &&
              value.id !== 'status-noshow',
          )
          .map(([, value]) => value.id.replace('status-', ''))

        contextMenuObj?.current?.showItems(
          [
            APPT_STATUS_MENU_ITEMS[statusToBeShown].id.replace('status-', ''),
            'notify',
            'call',
            'message',
            'cancel',
          ],
          true,
        )

        if (eventObj.appt_status === AppointmentStatus.Completed) {
          statussesToBeHidden.push(
            APPT_STATUS_MENU_ITEMS[AppointmentStatus.Confirmed].id.replace(
              'status-',
              '',
            ),
          )
        }

        contextMenuObj?.current?.hideItems(statussesToBeHidden, true)
      }
      const existingTooltips = document.querySelectorAll(
        '.e-tooltip-wrap.e-popup-open:has(.appt-tooltip-wrapper)',
      ) as unknown as HTMLElement[]
      existingTooltips.forEach((tooltip) => {
        tooltip.style.display = 'none'
      })
      return
    } else {
      args.cancel = true
    }
  }

  const selectPatient = (patientId: string): Promise<void> => {
    return fetchPatientById(patientId).then((response) => {
      if (response.statusCode !== 200 || !response.data?.patient) {
        throw new Error(response.message || 'Unable to load patient')
      }
      const contact = contacts?.find(
        (c) =>
          c.patient_id ===
          (response.data.patient as PatientWithInsurance).patient_id,
      )
      dispatch(
        setSelectedPatient({ patient: response.data.patient as any, contact }),
      )
      dispatch(toggleSidePanel(true))
    })
  }

  const getPreviousPageState = (): any => {
    const contentElement = document.querySelector('.e-content-wrap')
    const scrollTop = contentElement?.scrollTop
    const scrollLeft = contentElement?.scrollLeft

    const providerElement = document.querySelector(
      'select#providerSelect',
    ) as HTMLSelectElement | null
    const providerId = providerElement?.value

    return {
      path: '/appointment',
      calendarView: scheduleObj.current?.currentView,
      startDate: scheduleObj.current?.activeView.startDate(),
      endDate: scheduleObj.current?.activeView.endDate(),
      scrollTop,
      scrollLeft,
      providerId,
      hipaaView: isHIPAAView,
    }
  }

  const handlePhoneClick = (patientId: string): void => {
    const patientContact = contactsHooks.filteredContacts.find(
      (contact) => contact.patient_id === patientId,
    )
    if (patientContact) {
      contactsHooks.handlePhoneAction(patientContact)
    }
  }

  const handleMessageClick = (patientId: string): void => {
    const patientContact = contactsHooks.filteredContacts.find(
      (contact) => contact.patient_id === patientId,
    )
    const currentPageState = getPreviousPageState()
    if (patientContact) {
      contactsHooks.handleSendMessage(patientContact)
      navigate('/message', {
        state: {
          previousPage: {
            ...currentPageState,
          },
        },
      })
    }
  }

  const notifyPatient = (eventObj: any, content: string): void => {
    showLoading({
      container: '.right-click-menu-container',
      hideElements: ['.calendar-container'],
    })
    const patientContact = contactsHooks.filteredContacts.find(
      (contact) => contact.patient_id === eventObj.patient_id,
    )
    if (patientContact) {
      const chat = conversations?.find((c: IChatConversation) => {
        const participant = c.chat_participant
        return participant && participant.length == 1 && participant[0].contact
          ? participant[0].contact.contact_id === patientContact.contact_id
          : false
      })

      const newData = {
        chat_direction: ChatDirection.outgoing,
        timestamp: new Date().toISOString(),
        content: content,
        html_content: content,
        chat_conversation_id: chat?.chat_conversation_id ?? '',
      }

      dispatch(
        createSms({
          ...newData,
          content: content,
          calls: undefined,
          receiver_id: patientContact.cellphone,
          sender_id: getEnv('REACT_APP_TWILIO_NUMBER'),
          send_status: SendStatus.queued,
        }),
      )
        .then(() => {
          setTimeout(() => {
            hideLoading('.right-click-menu-container')
            setNotifyPatientModal({
              show: false,
              appointmentInfo: undefined,
            })
            showToast(
              createSuccessToast(
                'Patient notified successfully',
                'Patient has been notified successfully.',
              ),
            )
          }, 2000)
        })
        .catch(() => {
          setTimeout(() => {
            hideLoading('.right-click-menu-container')
            setNotifyPatientModal({
              show: false,
              appointmentInfo: undefined,
            })
            showToast(
              createFailedToast(
                'Failed to notify patient',
                'Failed to notify patient. Please try again.',
              ),
            )
          }, 2000)
        })
    }
  }

  const updateAppointmentStatus = async (
    appointment_id: string,
    status: number,
  ): Promise<void> => {
    try {
      showLoading({
        container: '.right-click-menu-container',
        hideElements: ['.calendar-container'],
      })
      await handleUpdateAppointmentStatus(appointment_id, status)
      setTimeout(() => {
        hideLoading('.right-click-menu-container')
        showToast(
          createSuccessToast(
            'Appointment status updated successfully',
            'Appointment status has been updated successfully.',
          ),
        )
      }, 2000)
    } catch (error) {
      logger.error('Error updating appointment status:', error)
      setTimeout(() => {
        hideLoading('.right-click-menu-container')
        showToast(
          createFailedToast(
            'Failed to update appointment status',
            'Failed to update appointment status. Please try again.',
          ),
        )
      }, 2000)
    }
  }

  const contextMenuSelect = async (
    args: ContextMenuEventArgs,
  ): Promise<void> => {
    onItemSelected?.()
    const selectedMenuItem: string = args.item.id as string
    let eventObj: Record<string, any> = {}
    if (selectedTarget && selectedTarget.classList.contains('e-appointment')) {
      eventObj = scheduleObj?.current?.getEventDetails(
        selectedTarget,
      ) as Record<string, any>
    }
    if (Object.keys(eventObj).length === 0) {
      return
    }
    const currentPageState = getPreviousPageState()
    switch (selectedMenuItem) {
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.Confirmed].id:
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.Unconfirmed].id:
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.Late].id:
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.NoShow].id:
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckIn].id:
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.InTreatment].id:
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.CheckOut].id:
      case APPT_STATUS_MENU_ITEMS[AppointmentStatus.Completed].id:
        const statusObj = Object.entries(APPT_STATUS_MENU_ITEMS).find(
          ([, value]) => value.id === selectedMenuItem,
        )
        if (statusObj) {
          await updateAppointmentStatus(
            eventObj.appointment_id,
            Number(statusObj[0]),
          )
        }
        break
      case 'goto-treatmentplan':
        selectPatient(eventObj.patient_id).then(() => {
          navigate('/charting/treatment-plan', {
            state: {
              previousPage: {
                ...currentPageState,
              },
            },
          })
        })
        break
      case 'goto-periocharting':
        selectPatient(eventObj.patient_id).then(() => {
          navigate('/charting/perio', {
            state: {
              previousPage: {
                ...currentPageState,
              },
            },
          })
        })
        break
      case 'goto-checkcoverage':
        selectPatient(eventObj.patient_id).then(() => {
          navigate('/insurance', {
            state: {
              tabKey: 'coverage',
              previousPage: {
                ...currentPageState,
              },
            },
          })
        })
        break
      case 'goto-reschedule':
        scheduleObj?.current?.openEditor(eventObj, 'Save')
        break
      case 'goto-rqpreauth':
        navigate('/claims', {
          state: {
            showAddClaim: true,
            tabKey: 'pre-auth',
            previousPage: {
              ...currentPageState,
            },
          },
        })
        break
      case 'goto-rqclaim':
        navigate('/claims', {
          state: {
            showAddClaim: true,
            previousPage: {
              ...currentPageState,
            },
          },
        })
        break
      case 'info-patient':
        selectPatient(eventObj.patient_id)
        break
      case 'info-appt':
        scheduleObj?.current?.openEditor(eventObj, 'Save')
        break
      case 'call':
        handlePhoneClick(eventObj.patient_id)
        break
      case 'message':
        handleMessageClick(eventObj.patient_id)
        break
      case 'email':
        openEmailModal(eventObj.patient.email)
        break
      case 'referto':
        openEmailModal('')
        break
      case 'notify':
        setNotifyPatientModal({
          show: true,
          appointmentInfo: eventObj,
        })
        break
      case 'addnote':
        if (onViewNote) {
          onViewNote({
            appointmentId: eventObj.appointment_id as string,
            patientId: eventObj.patient_id as string,
            contextName: formatAppointmentContextName(eventObj),
          })
        }
        break
      case 'cancel':
        openDeleteModal(eventObj.appointment_id)
        break
    }

    const statusSelected = Object.entries(APPT_STATUS_MENU_ITEMS).find(
      ([, value]) =>
        value.id.replace('status-', '') === selectedMenuItem.toString(),
    )

    if (statusSelected) {
      await updateAppointmentStatus(
        eventObj.appointment_id,
        Number(statusSelected[0]),
      )
    }
  }

  return (
    <>
      {/* Syncfusion moves its menu element into a body-level wrapper. Keep a
          stable React-owned sibling so scheduler remounts can insert safely. */}
      <div className="schedule-context-menu-host">
        <ContextMenuComponent
          id="overviewContextMenu"
          cssClass="schedule-context-menu"
          ref={contextMenuObj}
          target={targetCalendarContainer}
          items={contextMenuItems}
          beforeOpen={contextMenuOpen}
          select={contextMenuSelect}
        />
      </div>
      {contactsHooks.showSelectContactModal && (
        <SelectContactModal
          show={contactsHooks.showSelectContactModal}
          onHide={contactsHooks.handleCloseSelectContactModal}
          contactData={contactsHooks.selectedContact as IContact}
        />
      )}
      {notifyPatientModal.show && (
        <NotifyPatient
          show={notifyPatientModal.show}
          onHide={() =>
            setNotifyPatientModal({ show: false, appointmentInfo: undefined })
          }
          data={notifyPatientModal.appointmentInfo}
          onNotify={(data: any, content: string) =>
            notifyPatient(data, content)
          }
        />
      )}
    </>
  )
}

export default RightClickMenu
